import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import {
  buildStatement,
  computeBalance,
  formatDateTime,
  formatMoney,
  nextDueDate,
  TX_TYPE_META,
  type CreditCustomer,
  type CreditReminder,
  type CreditTransaction,
} from '@/lib/credit';

interface MerchantInfo {
  id: number;
  nomcommerce: string | null;
  name: string | null;
  telephone: string | null;
  monaie: string | null;
}

export default function CreditPortalPage() {
  const { user, loading: authLoading } = useAuth();
  const { brand } = useBrand();
  const [accounts, setAccounts] = useState<CreditCustomer[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [merchants, setMerchants] = useState<MerchantInfo[]>([]);
  const [reminders, setReminders] = useState<CreditReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: accs, error: accError } = await supabase
        .from('credit_customers')
        .select('*')
        .eq('linked_user_id', user.id)
        .order('created_at', { ascending: false });
      if (accError) throw accError;

      const list = (accs as CreditCustomer[]) || [];
      setAccounts(list);
      const ids = list.map((a) => a.id);

      if (ids.length > 0) {
        const [txRes, remRes] = await Promise.all([
          supabase.from('credit_transactions').select('*').in('customer_id', ids).order('created_at', { ascending: false }),
          supabase.from('credit_reminders').select('*').in('customer_id', ids).order('created_at', { ascending: false }).limit(30),
        ]);
        setTransactions((txRes.data as CreditTransaction[]) || []);
        setReminders((remRes.data as CreditReminder[]) || []);

        const merchantIds = [...new Set(list.map((a) => a.merchant_id))];
        const { data: mer } = await supabase
          .from('users')
          .select('id, nomcommerce, name, telephone, monaie')
          .in('id', merchantIds);
        setMerchants((mer as MerchantInfo[]) || []);
      } else {
        setTransactions([]);
        setReminders([]);
        setMerchants([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const merchantFor = (id: number) => merchants.find((m) => m.id === id);
  const txFor = (customerId: number) => transactions.filter((t) => t.customer_id === customerId);

  const totalOwed = useMemo(
    () => accounts.reduce((sum, a) => sum + Math.max(0, computeBalance(txFor(a.id))), 0),
    [accounts, transactions]
  );

  const downloadStatement = (account: CreditCustomer) => {
    const txs = txFor(account.id);
    const text = buildStatement({
      merchantName: merchantFor(account.merchant_id)?.nomcommerce || 'Commerce Zifek',
      customerName: account.full_name,
      currency: merchantFor(account.merchant_id)?.monaie || brand.name || 'MAD',
      balance: computeBalance(txs),
      creditLimit: account.credit_limit,
      transactions: txs,
    });
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `releve-${account.full_name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            {brand.logo ? (
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-background-50 border border-background-200/50 flex items-center justify-center">
                <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain p-1" />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm font-heading">{brand.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <span className="text-base font-bold font-heading text-foreground-950">{brand.name}</span>
          </Link>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
            <i className="ri-book-2-line"></i>Mon crédit
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-1">Mon carnet de crédit</h1>
        <p className="text-sm text-foreground-500 mb-6">Suivez vos soldes et vos opérations chez vos commerçants de confiance.</p>

        {!user ? (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-primary-600"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-800 mb-2">Connectez-vous pour voir votre crédit</h2>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Votre crédit est personnel et privé. Connectez-vous pour consulter vos soldes et votre historique.
            </p>
            <Link to="/login-client" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline hover:bg-primary-600 transition-colors">
              <i className="ri-login-circle-line"></i>Se connecter
            </Link>
          </div>
        ) : error ? (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
            <i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i>
            <p className="text-sm text-foreground-600 mb-4">{error}</p>
            <button onClick={load} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">Réessayer</button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-store-2-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-800 mb-2">Aucun compte de crédit</h2>
            <p className="text-sm text-foreground-500 max-w-md mx-auto">
              Scannez le QR d&apos;un commerçant ou demandez-lui de vous inviter pour lier votre compte.
            </p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="text-xs text-foreground-500 mb-1">Total à payer</div>
                <div className="text-2xl font-bold font-heading text-primary-700">{formatMoney(totalOwed, merchants[0]?.monaie || 'MAD')}</div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="text-xs text-foreground-500 mb-1">Commerçants liés</div>
                <div className="text-2xl font-bold font-heading text-foreground-950">{accounts.length}</div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="text-xs text-foreground-500 mb-1">Opérations</div>
                <div className="text-2xl font-bold font-heading text-foreground-950">{transactions.length}</div>
              </div>
            </div>

            {/* Accounts */}
            <div className="space-y-4">
              {accounts.map((account) => {
                const merchant = merchantFor(account.merchant_id);
                const txs = txFor(account.id);
                const balance = computeBalance(txs);
                const cur = merchant?.monaie || 'MAD';
                const due = nextDueDate(account.usual_payment_date);
                const isOpen = expanded === account.id;
                return (
                  <div key={account.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                            <i className="ri-store-2-line text-lg"></i>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-foreground-950 truncate">{merchant?.nomcommerce || merchant?.name || 'Commerce Zifek'}</h3>
                            {merchant?.telephone && <p className="text-xs text-foreground-400 truncate">{merchant.telephone}</p>}
                          </div>
                        </div>
                        <span className={`text-lg font-bold font-heading flex-shrink-0 ${balance > 0 ? 'text-primary-700' : 'text-accent-600'}`}>
                          {formatMoney(balance, cur)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="bg-background-100 rounded-lg px-3 py-2">
                          <div className="text-[10px] text-foreground-400 uppercase">Solde</div>
                          <div className={`text-sm font-semibold ${balance > 0 ? 'text-primary-700' : 'text-accent-600'}`}>{formatMoney(balance, cur)}</div>
                        </div>
                        <div className="bg-background-100 rounded-lg px-3 py-2">
                          <div className="text-[10px] text-foreground-400 uppercase">Plafond</div>
                          <div className="text-sm font-semibold text-foreground-800">{account.credit_limit != null ? formatMoney(account.credit_limit, cur) : '—'}</div>
                        </div>
                        <div className="bg-background-100 rounded-lg px-3 py-2">
                          <div className="text-[10px] text-foreground-400 uppercase">Disponible</div>
                          <div className="text-sm font-semibold text-foreground-800">{account.credit_limit != null ? formatMoney(Math.max(0, account.credit_limit - balance), cur) : '—'}</div>
                        </div>
                        <div className="bg-background-100 rounded-lg px-3 py-2">
                          <div className="text-[10px] text-foreground-400 uppercase">Échéance</div>
                          <div className="text-sm font-semibold text-foreground-800">{due ? new Date(due).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—'}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpanded(isOpen ? null : account.id)}
                          className="flex-1 h-9 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-1.5">
                          <i className={isOpen ? 'ri-arrow-up-s-line' : 'ri-history-line'}></i>{isOpen ? 'Masquer' : `Historique (${txs.length})`}
                        </button>
                        <button onClick={() => downloadStatement(account)}
                          className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-1.5">
                          <i className="ri-download-2-line"></i>Relevé
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="border-t border-background-200/70 divide-y divide-background-200/70">
                        {txs.length === 0 ? (
                          <p className="py-8 text-center text-sm text-foreground-400">Aucune opération</p>
                        ) : txs.map((tx) => {
                          const meta = TX_TYPE_META[tx.type];
                          const sign = tx.type === 'payment' ? '-' : Number(tx.amount) < 0 ? '-' : '+';
                          return (
                            <div key={tx.id} className="flex items-center gap-3 px-5 py-3">
                              <div className={`w-8 h-8 rounded-full ${meta.className} flex items-center justify-center flex-shrink-0`}>
                                <i className={`${meta.icon} text-sm`}></i>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-foreground-800 truncate">{meta.label}{tx.description ? ` · ${tx.description}` : ''}</div>
                                <div className="text-[11px] text-foreground-400">{formatDateTime(tx.created_at)}</div>
                              </div>
                              <span className={`text-sm font-semibold flex-shrink-0 ${tx.type === 'payment' ? 'text-accent-600' : tx.type === 'credit' ? 'text-primary-700' : 'text-secondary-600'}`}>
                                {sign}{formatMoney(Math.abs(Number(tx.amount)), cur)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Notifications */}
            {reminders.length > 0 && (
              <div className="mt-6 bg-background-50 border border-background-200/70 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-notification-3-line text-primary-500"></i>Notifications</h3>
                <div className="space-y-2.5">
                  {reminders.slice(0, 10).map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-foreground-700 truncate capitalize">{r.type.replace(/_/g, ' ')}</span>
                      <span className="text-[11px] text-foreground-400 flex-shrink-0">{formatDateTime(r.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 text-[11px] text-foreground-400 flex items-start gap-2">
              <i className="ri-shield-check-line mt-0.5 text-accent-500"></i>
              Vos données de crédit sont privées : vous ne voyez que vos propres comptes, et chaque commerçant ne voit que
              la relation qui le concerne. Aucune information d&apos;identité sensible n&apos;est demandée.
            </p>
          </>
        )}
      </main>
    </div>
  );
}